import type { FeedItem } from '@/components/ActivityFeed/ActivityFeed';
import { fmtValue } from '@/components/ActivityFeed/normalize';
import {
  CLIENT_COMPANY_STATUS_LABELS,
  COMPANY_FIELD_LABELS,
  type ClientCompanyStatus,
  type CompanyEvent,
  DOCUMENT_REQUIREMENT_LABELS,
  PARTY_ROLE_LABELS,
  type PartyRole,
} from '@/models/clientCompanies';

const statusLabel = (s: unknown) =>
  CLIENT_COMPANY_STATUS_LABELS[s as ClientCompanyStatus] ?? String(s ?? '-');

const DATE_FIELDS = new Set(['registrationDate', 'transferDate']);

const fieldValue = (field: string, v: unknown) => {
  if (v == null || v === '') return '-';
  if (DATE_FIELDS.has(field)) {
    const [y, m, d] = String(v).slice(0, 10).split('-');
    return d ? `${d}.${m}.${y}` : String(v);
  }
  return fmtValue(v).split(String.fromCharCode(8230)).join('...');
};

const roleLabel = (r: unknown) =>
  PARTY_ROLE_LABELS[r as PartyRole] ?? String(r ?? '');

export function buildCompanyFeed(events: CompanyEvent[]): FeedItem[] {
  return events.map((e) => {
    const p = e.payload ?? {};
    const base = {
      id: `event-${e.id}`,
      at: e.createdAt,
      actor: e.actorName ?? e.actorEmail ?? null,
    };
    switch (e.kind) {
      case 'created':
        return { ...base, kind: 'system', title: 'Created on Settle' };
      case 'imported':
        return { ...base, kind: 'system', title: 'Imported from Monday' };
      case 'status_changed':
        return {
          ...base,
          kind: 'status',
          title: 'Status changed',
          changes: [
            {
              label: 'Status',
              from: statusLabel(p.from),
              to: statusLabel(p.to),
            },
          ],
        };
      case 'field_updated':
        return {
          ...base,
          kind: 'update',
          title: 'Updated',
          changes: [
            {
              label: COMPANY_FIELD_LABELS[p.field as string] ?? String(p.field),
              from: fieldValue(String(p.field), p.from),
              to: fieldValue(String(p.field), p.to),
            },
          ],
        };
      case 'party_linked':
        return {
          ...base,
          kind: 'update',
          title: `${roleLabel(p.role)} added${p.contact ? ' (new contact)' : ''}${p.externalCompany ? ' (new external company)' : ''}`,
        };
      case 'party_unlinked':
        return {
          ...base,
          kind: 'update',
          title: `${roleLabel(p.role)} removed`,
        };
      case 'document_linked':
        return {
          ...base,
          kind: 'update',
          title: `Document linked${p.requirement ? `: ${DOCUMENT_REQUIREMENT_LABELS[p.requirement as string] ?? p.requirement}` : ''}`,
        };
      case 'services_added':
        return { ...base, kind: 'system', title: 'Services added on Settle' };
      case 'several_registrations':
        return {
          ...base,
          kind: 'system',
          title: `Offer #${p.offerId} bought ${p.count} company registrations - only this company was created`,
          body: Array.isArray(p.services)
            ? (p.services as { serviceName?: string; quantity?: number }[])
                .map((s) => `${s.serviceName ?? '-'} x ${s.quantity ?? 1}`)
                .join('\n')
            : undefined,
        };
      default:
        return { ...base, kind: 'system', title: e.kind };
    }
  }) as FeedItem[];
}
